// Single home for translating a warehouse document line (WZ / PZ) into the
// shared "Przelicznik jednostek" editor value and back.
//
// The two models differ on purpose:
//
//   line     : quantity + unit (the product's BASE UNIT — stock moves in it)
//              + conversionUnit (optional m²/m³ shown on the form and PDF)
//   editor   : physicalQuantity/physicalUnit + pricingQuantity/pricingUnit
//
// Only `quantity`, `unit` and `conversionUnit` are persisted. The converted
// amount is ALWAYS recomputed from the line's dimensions (backend
// documentPdfService does the same), so the form, the PDF and the DB can never
// disagree — and a rounding choice made in the editor stays consistent, because
// the number it rounded to is the one we store.
import {
  AllocationUnit,
  ConversionSource,
  WZConversionUnit,
  computeConversionValue,
  normalizeConversionUnit,
} from '../../utils/dimensionConversion';
import {
  CommercialQuantityValue,
  MAX_CUSTOMER_PRICING_QUANTITY,
  asCommercialUnit,
  roundCommercialQuantity,
} from '../../utils/commercialQuantity';
import { formatUnit, normalizeUnitCode } from '../../utils/units';
import { WAREHOUSE_QUANTITY_PRECISION } from '../../utils/warehouseQuantity';
import { localeForLanguage } from '../../utils/numberFormatting';

export interface WarehouseQuantityLine extends ConversionSource {
  quantity?: number | null;
  unit?: string | null;
  conversionUnit?: WZConversionUnit | null;
}

export interface WarehouseQuantityPatch {
  quantity: number;
  conversionUnit: WZConversionUnit | null;
  /** Present only for free-text lines, whose unit stays operator-editable. */
  unit?: string;
}

// Converted (m²/m³) amounts are derived for display and never persisted, so the
// only bound that applies is what the shared editor can represent. Single home
// for all three warehouse call sites (WZ create, WZ detail, PZ items).
export const WAREHOUSE_CONVERTED_QUANTITY_MAX = MAX_CUSTOMER_PRICING_QUANTITY;

/**
 * The unit a line's quantity is denominated in, as an editor-compatible code.
 *
 * This reads the LINE's own unit, which is what the quantity means today. Lines
 * carry either a warehouse code ('pcs', 'm2', …) or the raw `ProductUnit` enum
 * ('PIECE', 'SQUARE_METER') copied off the product's baseUnit; normalizeUnitCode
 * collapses both. Units the conversion model does not cover (kg, m, l) fall back
 * to pieces so the editor still opens; those lines simply get no conversion
 * option, which is correct — you cannot derive an area from a weight.
 */
export function getWarehouseLineUnit(line: WarehouseQuantityLine): AllocationUnit {
  return asCommercialUnit(normalizeUnitCode(line.unit) ?? 'pcs');
}

/** True when the line's base unit supports an m²/m³ conversion at all. */
export function supportsWarehouseConversion(line: WarehouseQuantityLine): boolean {
  const code = normalizeUnitCode(line.unit) ?? 'pcs';
  return code === 'pcs' || code === 'm2' || code === 'm3';
}

/** Line -> editor value. */
export function buildWarehouseQuantityValue(line: WarehouseQuantityLine): CommercialQuantityValue {
  const physicalUnit = getWarehouseLineUnit(line);
  const physicalQuantity = Number(line.quantity) || 0;
  const conversionUnit = normalizeConversionUnit(line.conversionUnit);

  if (!conversionUnit || conversionUnit === physicalUnit) {
    return {
      physicalQuantity,
      physicalUnit,
      pricingQuantity: physicalQuantity,
      pricingUnit: physicalUnit,
    };
  }

  const converted = computeConversionValue(conversionUnit, { ...line, unit: physicalUnit }, physicalQuantity);

  return {
    physicalQuantity,
    physicalUnit,
    // A line whose dimensions cannot produce the conversion still keeps the
    // operator's chosen unit — the form shows "brak wymiarów" rather than
    // silently dropping the choice.
    pricingQuantity: converted === null ? 0 : roundCommercialQuantity(converted),
    pricingUnit: conversionUnit,
  };
}

/**
 * One warehouse quantity with its unit, Polish-formatted.
 *
 * Single home for "a number next to a unit" on a warehouse line. Interpolating
 * the raw number renders 3.75 with an English dot right beside a Polish `m²`,
 * while the totals row and the PDF next to it print 3,75 — two decimal
 * separators on one screen.
 */
export function formatWarehouseQuantity(
  quantity: number | null | undefined,
  unit: string | null | undefined,
  language = 'pl',
): string {
  const amount = (Number(quantity) || 0).toLocaleString(localeForLanguage(language), {
    maximumFractionDigits: WAREHOUSE_QUANTITY_PRECISION,
  });

  return `${amount} ${formatUnit(unit, language)}`;
}

/**
 * "RAZEM" for a document's quantity column.
 *
 * A document may mix units (a pieces line next to an m² line) and those cannot
 * collapse into one number, so each unit is totalled separately. The frontend
 * twin of buildWarehouseQuantityTotalLabel in backend documentPdfService.js —
 * the screen and the PDF must agree.
 */
export function formatWarehouseQuantityTotals(
  lines: WarehouseQuantityLine[],
  language = 'pl',
): string {
  const totalsByUnit = new Map<string, number>();

  lines.forEach((line) => {
    const unit = normalizeUnitCode(line.unit) ?? 'pcs';
    totalsByUnit.set(unit, (totalsByUnit.get(unit) || 0) + (Number(line.quantity) || 0));
  });

  return Array.from(totalsByUnit.entries())
    .filter(([, total]) => total)
    .map(([unit, total]) => formatWarehouseQuantity(total, unit, language))
    .join(', ');
}

/**
 * Editor value -> the two fields the line actually persists.
 *
 * `pricingQuantity` is deliberately discarded: it is a derived presentation
 * number, and storing it would let it drift from the dimensions it came from.
 */
export function buildWarehouseQuantityPatch(value: CommercialQuantityValue): WarehouseQuantityPatch {
  const conversionUnit = value.pricingUnit === value.physicalUnit
    ? null
    : normalizeConversionUnit(value.pricingUnit);

  return {
    quantity: roundCommercialQuantity(value.physicalQuantity),
    conversionUnit,
  };
}
